import React, { useEffect, useMemo, useState } from "react";
import { api, fmtEUR, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { StatusBadge } from "../components/StatusBadge";
import { ChevronLeft, ChevronRight, GripVertical, User } from "lucide-react";
import BookingModal from "../components/BookingModal";
import { toast } from "sonner";

function startOfWeek(d) {
  const x = new Date(d);
  const day = (x.getDay() + 6) % 7; // Monday=0
  x.setDate(x.getDate() - day);
  x.setHours(0,0,0,0);
  return x;
}
function addDays(d, n) { const x = new Date(d); x.setDate(x.getDate() + n); return x; }
function iso(d) {
  // Local date-only ISO to avoid UTC shift (e.g. Italy UTC+1/+2 makes toISOString()
  // return previous day for a local midnight date).
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`;
}
const DAYS = ["Lun","Mar","Mer","Gio","Ven","Sab","Dom"];

export default function Calendario() {
  const [weekStart, setWeekStart] = useState(startOfWeek(new Date()));
  const [bookings, setBookings] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [editing, setEditing] = useState(null);
  const [dragDriverId, setDragDriverId] = useState(null);

  const load = async () => {
    const from = iso(weekStart);
    const to = iso(addDays(weekStart, 6));
    const [b, d, v] = await Promise.all([
      api.get("/bookings", { params: { date_from: from, date_to: to } }),
      api.get("/drivers"),
      api.get("/vehicles"),
    ]);
    setBookings(b.data.filter(x => x.status !== "Cancellata"));
    setDrivers(d.data);
    setVehicles(v.data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [weekStart]);

  const byDay = useMemo(() => {
    const m = {};
    for (let i = 0; i < 7; i++) m[iso(addDays(weekStart, i))] = [];
    bookings.forEach(b => { if (m[b.date]) m[b.date].push(b); });
    Object.values(m).forEach(list => list.sort((a,b) => (a.time||"").localeCompare(b.time||"")));
    return m;
  }, [bookings, weekStart]);

  const driverName = (id) => { const d = drivers.find(x=>x.id===id); return d ? `${d.first_name} ${d.last_name}` : null; };
  const vehicleName = (id) => { const v = vehicles.find(x=>x.id===id); return v ? `${v.brand} ${v.model}` : ""; };

  const onDropDriver = async (booking) => {
    if (!dragDriverId) return;
    try {
      const newStatus = booking.status === "Preventivo" || booking.status === "Confermata" ? "Assegnata" : booking.status;
      await api.patch(`/bookings/${booking.id}`, { driver_id: dragDriverId, status: newStatus });
      toast.success("Autista assegnato");
      setDragDriverId(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const label = `${weekStart.toLocaleDateString("it-IT",{day:"2-digit",month:"short"})} — ${addDays(weekStart,6).toLocaleDateString("it-IT",{day:"2-digit",month:"short",year:"numeric"})}`;

  return (
    <div className="space-y-5" data-testid="calendario-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-white">Calendario</h1>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-mono-num mt-1">{label}</div>
        </div>
        <div className="flex gap-2">
          <Button data-testid="cal-prev" variant="outline" className="border-slate-800 bg-transparent text-slate-300 hover:bg-slate-900" onClick={()=>setWeekStart(addDays(weekStart,-7))}><ChevronLeft size={16}/> Prec.</Button>
          <Button variant="outline" className="border-slate-800 bg-transparent text-slate-300 hover:bg-slate-900" onClick={()=>setWeekStart(startOfWeek(new Date()))}>Oggi</Button>
          <Button data-testid="cal-next" variant="outline" className="border-slate-800 bg-transparent text-slate-300 hover:bg-slate-900" onClick={()=>setWeekStart(addDays(weekStart,7))}>Succ. <ChevronRight size={16}/></Button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[240px_1fr] gap-4">
        <aside className="rounded-2xl border border-slate-800/80 bg-slate-900/40 p-4 h-fit">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num mb-3">Autisti · trascina</div>
          <div className="space-y-1.5">
            {drivers.length === 0 && <div className="text-xs text-slate-500">Nessun autista. Creali dalla sezione Autisti.</div>}
            {drivers.map(d => (
              <div
                key={d.id}
                draggable
                data-testid={`draggable-driver-${d.id}`}
                onDragStart={() => setDragDriverId(d.id)}
                onDragEnd={() => setDragDriverId(null)}
                className="flex items-center gap-2 px-2.5 py-2 rounded-lg bg-slate-950/60 border border-slate-800 text-sm text-slate-200 hover:border-amber-500/40 cursor-grab active:cursor-grabbing"
              >
                <GripVertical size={14} className="text-slate-600"/>
                <div className="w-6 h-6 rounded-full bg-amber-500/20 text-amber-400 grid place-items-center text-[10px] font-bold">
                  {(d.first_name||"?").slice(0,1)}{(d.last_name||"").slice(0,1)}
                </div>
                <div className="flex-1 truncate">{d.first_name} {d.last_name}</div>
                <StatusBadge status={d.status} />
              </div>
            ))}
          </div>
        </aside>

        <div className="grid grid-cols-1 md:grid-cols-7 gap-2">
          {[0,1,2,3,4,5,6].map(i => {
            const d = addDays(weekStart, i);
            const key = iso(d);
            const items = byDay[key] || [];
            const isToday = key === iso(new Date());
            return (
              <div key={key} className={`rounded-2xl border ${isToday ? "border-amber-500/40" : "border-slate-800/80"} bg-slate-900/40 flex flex-col min-h-[300px]`}>
                <div className={`px-3 py-2 border-b ${isToday ? "border-amber-500/30" : "border-slate-800/80"} flex items-center justify-between`}>
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-mono-num">{DAYS[i]}</div>
                  <div className={`font-mono-num font-bold ${isToday ? "text-amber-400" : "text-slate-200"}`}>{d.getDate()}</div>
                </div>
                <div className="p-2 space-y-1.5 flex-1">
                  {items.length === 0 && <div className="text-[11px] text-slate-600 text-center py-6">—</div>}
                  {items.map(b => (
                    <div
                      key={b.id}
                      data-testid={`cal-booking-${b.id}`}
                      onClick={()=>setEditing(b)}
                      onDragOver={(e)=>{e.preventDefault();}}
                      onDrop={()=>onDropDriver(b)}
                      className="group rounded-lg border border-slate-800 bg-slate-950/60 hover:bg-slate-900 cursor-pointer p-2 text-xs"
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-mono-num text-amber-400">{b.time}</span>
                        <StatusBadge status={b.status}/>
                      </div>
                      <div className="mt-1 text-white truncate">{b.passenger_name || "-"}</div>
                      <div className="text-slate-500 truncate text-[11px]">{b.pickup} → {b.destination}</div>
                      <div className="mt-1 flex items-center justify-between">
                        <span className="text-[11px] text-slate-400 inline-flex items-center gap-1">
                          <User size={11}/> {driverName(b.driver_id) || <span className="text-rose-400">Da assegnare</span>}
                        </span>
                        <span className="text-[11px] text-emerald-400 font-mono-num">{fmtEUR(b.price)}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {editing && <BookingModal booking={editing} onClose={(s)=>{setEditing(null); if(s){toast.success("Salvata"); load();}}}/>}
    </div>
  );
}
